import React, { FC } from 'react';
import { Modal } from 'antd';
import { ReactComponent as CloseIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/close.svg';
import { ReactComponent as FreeStarIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/free.svg';
import { ReactComponent as OnlineZoomIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/online-via-zoom.svg';
import { ReactComponent as CalendarIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/let-choose-time.svg';
import { ReactComponent as SendOfferIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/package-offer/send-offer.svg';
import type { FormattedTrainerSchedule, ScheduleDayItem } from '../../types';
import './InviteFreeCallModal.scss';

interface InviteFreeCallModalProps {
  isOpen: boolean;
  onClose: () => void;
  clientName?: string;
  scheduleData: FormattedTrainerSchedule;
  onChangeSchedule?: () => void;
  onConfirmSend: (invitationData: {
    callTitle: string;
    scheduleSummary: string;
    scheduleDays?: ScheduleDayItem[];
    timezone: string;
  }) => void;
  isSending?: boolean;
}

export const InviteFreeCallModal: FC<InviteFreeCallModalProps> = ({
  isOpen,
  onClose,
  clientName = 'Client',
  scheduleData,
  onChangeSchedule,
  onConfirmSend,
  isSending = false,
}) => {
  if (!isOpen) return null;

  const clientFirstName = clientName.split(' ')[0] || 'Client';

  const handleSend = () => {
    const summary = scheduleData.isUniform
      ? scheduleData.uniformText || 'Monday to Friday • 09:00 - 17:00'
      : scheduleData.daysList.map((d) => `${d.day}: ${d.time}`).join(' | ');

    onConfirmSend({
      callTitle: '15-min introductory call',
      scheduleSummary: summary,
      scheduleDays: !scheduleData.isUniform ? scheduleData.daysList : undefined,
      timezone: scheduleData.timezone,
    });
  };

  return (
    <Modal
      open={isOpen}
      footer={null}
      onCancel={onClose}
      centered
      width={540}
      closeIcon={null}
      rootClassName="invite-free-call-modal"
      className="invite-free-call-modal"
      styles={{
        mask: {
          backdropFilter: 'blur(8px)',
          backgroundColor: 'rgba(6, 7, 35, 0.8)',
        },
        container: {
          background: '#060821',
          backgroundColor: '#060821',
          border: '1px solid #1b1c2f',
          borderRadius: '20px',
          boxShadow: '0 24px 60px rgba(0, 0, 0, 0.75)',
          padding: '24px',
          color: '#ffffff',
          overflow: 'visible',
        },
        body: {
          padding: 0,
          background: 'transparent',
        },
      }}
    >
      <div className="invite-call-content">
        {/* Modal Top Bar */}
        <div className="modal-top-bar">
          <h2 className="modal-heading">{'Invite to a free call'}</h2>
          <button type="button" className="close-btn" onClick={onClose} aria-label="Close modal">
            <CloseIcon />
          </button>
        </div>

        {/* Inner Card */}
        <div className="call-details-card">
          <div className="card-header-info">
            <h3 className="call-title">{'15-min introductory call'}</h3>
            <p className="call-subtitle">
              {"A chance to discuss your goals and see if we're a good fit"}
            </p>
          </div>

          <div className="card-divider" />

          {/* Badges Row */}
          <div className="badges-row">
            <div className="call-pill-badge">
              <FreeStarIcon className="badge-svg" />
              <span>{'Free'}</span>
            </div>
            <div className="call-pill-badge">
              <OnlineZoomIcon className="badge-svg" />
              <span>{'Online via Zoom'}</span>
            </div>
          </div>

          <div className="card-divider" />

          {/* Availability note */}
          <div className="availability-intro-row">
            <CalendarIcon className="cal-icon" />
            <span>{`Let ${clientFirstName} choose a time from your availability.`}</span>
          </div>

          {/* Schedule Box */}
          <div className="schedule-info-box">
            <div className="schedule-left-col">
              {scheduleData.isUniform ? (
                <span className="schedule-hours">{scheduleData.uniformText}</span>
              ) : (
                <div className="schedule-days-list">
                  {scheduleData.daysList.map((item) => (
                    <div key={item.day} className="schedule-day-row">
                      <span className="day-name">{item.day}:</span>
                      <span className="day-time">{item.time}</span>
                    </div>
                  ))}
                </div>
              )}
              <span className="schedule-tz">{scheduleData.timezone}</span>
            </div>
            {onChangeSchedule && (
              <button type="button" className="btn-change-schedule" onClick={onChangeSchedule}>
                {'Change'}
              </button>
            )}
          </div>

          {/* Send Invitation Button */}
          <button
            type="button"
            className="btn-send-invitation"
            onClick={handleSend}
            disabled={isSending}
          >
            <SendOfferIcon className="send-icon" />
            <span>{isSending ? 'Sending Invitation...' : 'Send Invitation'}</span>
          </button>
        </div>
      </div>
    </Modal>
  );
};
